<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import SettingsHeader from 'dashboard/components-next/aifocus/pageComponents/settings/SettingsHeader.vue';

const props = defineProps({
  agentId: {
    type: Number,
    required: true,
  },
});

const store = useStore();

const agent = computed(() =>
  store.getters['aifocusAgents/getRecord'](props.agentId)
);

const teams = useMapGetter('teams/getTeams');
const crmAgents = useMapGetter('agents/getAgents');

const isSaving = ref(false);

// Atendimento state
const assignmentMode = ref('specific_agent');
const requiredAssigneeName = ref('I.A');
const autoAssignNew = ref(true);
const chatwootAgentId = ref(null);
const debounceEnabled = ref(true);
const debounceSeconds = ref(10);
const transferEnabled = ref(true);
const transferKeywords = ref('atendente, humano, pessoa');
const transferTeamId = ref(null);
const splitLongMessages = ref(true);
const maxMessageLength = ref(1000);
const welcomeMessage = ref('');
const handoffMessage = ref('');
const transferSummaryPrompt = ref('');

const assignmentModes = [
  {
    value: 'specific_agent',
    label: 'Atendente especifico',
    description:
      'So responde quando atribuido a um atendente com nome especifico',
  },
  {
    value: 'unassigned',
    label: 'Conversas nao atribuidas',
    description: 'Responde apenas conversas sem atendente',
  },
  {
    value: 'always',
    label: 'Sempre responder',
    description: 'Responde todas as conversas na inbox',
  },
];

// ComboBox options
const agentOptions = computed(() => {
  const opts = [{ value: '', label: 'Selecione um atendente...' }];
  if (crmAgents.value) {
    crmAgents.value.forEach(a => {
      opts.push({ value: a.id, label: a.name });
    });
  }
  return opts;
});

const teamOptions = computed(() => {
  const opts = [{ value: '', label: 'Nenhuma (atribuir manualmente)' }];
  if (teams.value) {
    teams.value.forEach(t => {
      opts.push({ value: t.id, label: t.name });
    });
  }
  return opts;
});

const loadFromAgent = agentData => {
  if (!agentData) return;
  const config = agentData.config || {};
  assignmentMode.value = config.assignment_mode || 'specific_agent';
  requiredAssigneeName.value = config.required_assignee_name || 'I.A';
  autoAssignNew.value = config.auto_assign_new ?? true;
  chatwootAgentId.value = config.chatwoot_agent_id || '';
  debounceEnabled.value = config.debounce_enabled ?? true;
  debounceSeconds.value = config.debounce_seconds || 10;
  transferEnabled.value = config.transfer_enabled ?? true;
  transferKeywords.value = (
    config.transfer_keywords || ['atendente', 'humano', 'pessoa']
  ).join(', ');
  transferTeamId.value = config.transfer_team_id || '';
  splitLongMessages.value = config.split_long_messages ?? true;
  maxMessageLength.value = config.max_message_length || 1000;
  welcomeMessage.value = config.welcome_message || '';
  handoffMessage.value = config.handoff_message || '';
  transferSummaryPrompt.value = config.transfer_summary_prompt || '';
};

watch(
  agent,
  newAgent => {
    if (newAgent) loadFromAgent(newAgent);
  },
  { immediate: true, deep: false }
);

// Load teams and agents
store.dispatch('teams/get');
store.dispatch('agents/get');

const handleSave = async () => {
  isSaving.value = true;
  try {
    const keywords = transferKeywords.value
      ? transferKeywords.value
          .split(',')
          .map(k => k.trim())
          .filter(Boolean)
      : [];

    await store.dispatch('aifocusAgents/update', {
      id: props.agentId,
      config: {
        ...(agent.value?.config || {}),
        assignment_mode: assignmentMode.value,
        required_assignee_name: requiredAssigneeName.value,
        auto_assign_new: autoAssignNew.value,
        chatwoot_agent_id: chatwootAgentId.value || null,
        debounce_enabled: debounceEnabled.value,
        debounce_seconds: Number(debounceSeconds.value),
        transfer_enabled: transferEnabled.value,
        transfer_keywords: keywords,
        transfer_team_id: transferTeamId.value || null,
        split_long_messages: splitLongMessages.value,
        max_message_length: Number(maxMessageLength.value),
        welcome_message: welcomeMessage.value || null,
        handoff_message: handoffMessage.value || null,
        transfer_summary_prompt: transferSummaryPrompt.value || null,
      },
    });
    await store.dispatch('aifocusAgents/sync', { id: props.agentId });
    await store.dispatch('aifocusAgents/show', props.agentId);
    useAlert('Atendimento salvo e sincronizado');
  } catch (error) {
    useAlert(error?.message || 'Erro ao salvar configurações');
  } finally {
    isSaving.value = false;
  }
};
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="pb-6">
    <SettingsHeader
      heading="Configurações de Atendimento"
      description="Defina como o agente se comporta ao receber e responder conversas."
    />

    <div class="mt-6 space-y-6">
      <!-- Assignment Mode -->
      <div class="p-5 bg-n-alpha-1 border border-n-weak rounded-xl space-y-4">
        <h4
          class="text-sm font-semibold text-n-slate-12 flex items-center gap-2"
        >
          <span class="i-lucide-user-check text-n-slate-11" />
          Modo de Atribuição
        </h4>

        <div class="grid gap-2">
          <button
            v-for="mode in assignmentModes"
            :key="mode.value"
            class="flex items-start gap-3 p-3 rounded-lg border transition-colors text-left w-full"
            :class="
              assignmentMode === mode.value
                ? 'border-n-brand bg-n-brand/5'
                : 'border-n-weak hover:border-n-slate-6'
            "
            @click="assignmentMode = mode.value"
          >
            <div
              class="w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center flex-shrink-0"
              :class="
                assignmentMode === mode.value
                  ? 'border-n-brand'
                  : 'border-n-slate-8'
              "
            >
              <div
                v-if="assignmentMode === mode.value"
                class="w-2 h-2 rounded-full bg-n-brand"
              />
            </div>
            <div>
              <p class="text-sm font-medium text-n-slate-12">
                {{ mode.label }}
              </p>
              <p class="text-xs text-n-slate-10">{{ mode.description }}</p>
            </div>
          </button>
        </div>

        <!-- Agent name (only for specific_agent mode) -->
        <div v-if="assignmentMode === 'specific_agent'">
          <Input
            v-model="requiredAssigneeName"
            label="Nome do atendente no CRM"
            placeholder="I.A"
            message="O agente so responde quando a conversa estiver atribuida a um atendente com esse nome exato."
          />
        </div>
      </div>

      <!-- Auto-assign new conversations -->
      <div class="p-5 bg-n-alpha-1 border border-n-weak rounded-xl space-y-4">
        <div class="flex items-center justify-between">
          <div>
            <h4
              class="text-sm font-semibold text-n-slate-12 flex items-center gap-2"
            >
              <span class="i-lucide-plus-circle text-n-slate-11" />
              Conversas Novas
            </h4>
            <p class="text-xs text-n-slate-10 mt-1">
              Quando uma nova conversa chegar, atribuir automaticamente ao
              agente
            </p>
          </div>
          <Switch v-model="autoAssignNew" />
        </div>

        <div v-if="autoAssignNew" class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            Atribuir ao atendente do CRM
          </label>
          <ComboBox
            v-model="chatwootAgentId"
            :options="agentOptions"
            placeholder="Selecione um atendente..."
            search-placeholder="Buscar atendente..."
          />
          <p class="text-xs text-n-slate-10">
            O atendente no CRM que sera atribuido automaticamente quando uma
            conversa nova chegar. Use o mesmo nome configurado acima (ex:
            "I.A").
          </p>
        </div>
      </div>

      <!-- Debounce -->
      <div class="p-5 bg-n-alpha-1 border border-n-weak rounded-xl space-y-4">
        <div class="flex items-center justify-between">
          <div>
            <h4
              class="text-sm font-semibold text-n-slate-12 flex items-center gap-2"
            >
              <span class="i-lucide-timer text-n-slate-11" />
              Debounce de Mensagens
            </h4>
            <p class="text-xs text-n-slate-10 mt-1">
              Aguardar antes de responder para agrupar mensagens do cliente
            </p>
          </div>
          <Switch v-model="debounceEnabled" />
        </div>

        <div v-if="debounceEnabled" class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            Tempo de espera (segundos)
          </label>
          <div class="flex items-center gap-3">
            <input
              v-model.number="debounceSeconds"
              type="range"
              min="3"
              max="30"
              class="flex-1"
            />
            <Input
              v-model="debounceSeconds"
              type="number"
              size="sm"
              custom-input-class="w-16 text-center"
              min="3"
              max="30"
            />
          </div>
        </div>
      </div>

      <!-- Transfer to human -->
      <div class="p-5 bg-n-alpha-1 border border-n-weak rounded-xl space-y-4">
        <div class="flex items-center justify-between">
          <div>
            <h4
              class="text-sm font-semibold text-n-slate-12 flex items-center gap-2"
            >
              <span class="i-lucide-arrow-right-left text-n-slate-11" />
              Transferencia para Humano
            </h4>
            <p class="text-xs text-n-slate-10 mt-1">
              Permitir que o cliente peca um atendente humano
            </p>
          </div>
          <Switch v-model="transferEnabled" />
        </div>

        <template v-if="transferEnabled">
          <Input
            v-model="transferKeywords"
            label="Palavras-chave de transferencia"
            placeholder="atendente, humano, pessoa"
            message="Separadas por virgula. Quando o cliente digitar essas palavras, a conversa sera transferida."
          />

          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium text-n-slate-12">
              Transferir para equipe
            </label>
            <ComboBox
              v-model="transferTeamId"
              :options="teamOptions"
              placeholder="Nenhuma (atribuir manualmente)"
              search-placeholder="Buscar equipe..."
            />
          </div>

          <TextArea
            v-model="handoffMessage"
            label="Mensagem de transferencia"
            placeholder="Estou transferindo voce para um atendente humano. Aguarde um momento."
            auto-height
            message="Enviada ao cliente quando a transferencia e iniciada."
          />

          <TextArea
            v-model="transferSummaryPrompt"
            label="Instrucoes para o resumo de transferencia"
            placeholder="Gere um resumo conciso da conversa incluindo: problema do cliente, acoes realizadas pela IA e status atual."
            auto-height
            message="Instrucoes para a IA gerar o resumo que sera enviado como nota privada ao atendente humano durante a transferencia."
          />
        </template>
      </div>

      <!-- Messages -->
      <div class="p-5 bg-n-alpha-1 border border-n-weak rounded-xl space-y-4">
        <h4
          class="text-sm font-semibold text-n-slate-12 flex items-center gap-2"
        >
          <span class="i-lucide-message-square text-n-slate-11" />
          Mensagens
        </h4>

        <TextArea
          v-model="welcomeMessage"
          label="Mensagem de boas-vindas"
          placeholder="Ola! Sou o assistente virtual. Como posso ajudar?"
          auto-height
          message="Enviada automaticamente na primeira mensagem de uma conversa nova."
        />
      </div>

      <!-- Response settings -->
      <div class="p-5 bg-n-alpha-1 border border-n-weak rounded-xl space-y-4">
        <h4
          class="text-sm font-semibold text-n-slate-12 flex items-center gap-2"
        >
          <span class="i-lucide-settings-2 text-n-slate-11" />
          Resposta
        </h4>

        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-n-slate-12">
              Dividir mensagens longas
            </p>
            <p class="text-xs text-n-slate-10">
              Quebra respostas grandes em mensagens menores
            </p>
          </div>
          <Switch v-model="splitLongMessages" />
        </div>

        <div v-if="splitLongMessages" class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            Tamanho maximo por mensagem
          </label>
          <div class="flex items-center gap-3">
            <input
              v-model.number="maxMessageLength"
              type="range"
              min="50"
              max="4000"
              step="50"
              class="flex-1"
            />
            <Input
              v-model="maxMessageLength"
              type="number"
              size="sm"
              custom-input-class="w-20 text-center"
              min="50"
              max="4000"
            />
          </div>
        </div>
      </div>

      <!-- Save Button -->
      <div class="flex justify-end">
        <Button
          label="Salvar Atendimento"
          :is-loading="isSaving"
          @click="handleSave"
        />
      </div>
    </div>
  </div>
</template>
